import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Mail, Send, FileText, CheckCircle, Clock, Users, 
  Search, RefreshCw, Eye, Building2, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailOutreachPage = () => {
  const [brokers, setBrokers] = useState([]);
  const [selectedBrokers, setSelectedBrokers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templatePreview, setTemplatePreview] = useState(null);
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('new');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [campaignDetailOpen, setCampaignDetailOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const fetchBrokers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      params.append('limit', '100');
      
      const response = await axios.get(`${API}/brokers?${params.toString()}`);
      setBrokers(response.data);
    } catch (error) {
      console.error('Error fetching brokers:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/email/templates`);
      setTemplates(response.data);
      if (response.data.length > 0) {
        setSelectedTemplate(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/email/stats`);
      setStats(response.data);
      setCampaigns(response.data.recent_campaigns || []);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchBrokers();
    fetchTemplates();
    fetchStats();
  }, [fetchBrokers]);

  const loadTemplatePreview = async (templateId) => {
    try {
      const response = await axios.get(`${API}/email/templates/${templateId}`);
      setTemplatePreview(response.data);
      setPreviewOpen(true);
    } catch (error) {
      toast.error('Failed to load template');
    }
  };

  const toggleSelectBroker = (brokerId) => {
    setSelectedBrokers(prev => 
      prev.includes(brokerId) 
        ? prev.filter(id => id !== brokerId)
        : [...prev, brokerId]
    );
  };

  const selectAllBrokers = () => {
    if (selectedBrokers.length === brokers.length) {
      setSelectedBrokers([]);
    } else {
      setSelectedBrokers(brokers.map(b => b.id));
    }
  };

  const sendOutreach = async () => {
    if (selectedBrokers.length === 0) {
      toast.error('Select at least one broker');
      return;
    }
    if (!selectedTemplate) {
      toast.error('Select an email template');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/email/outreach`, {
        broker_ids: selectedBrokers,
        template_id: selectedTemplate
      });
      
      toast.success(`Sent ${response.data.sent} emails, ${response.data.failed} failed`);
      setSelectedBrokers([]);
      fetchBrokers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  const viewCampaignDetails = async (campaign) => {
    try {
      const response = await axios.get(`${API}/email/campaigns/${campaign.id}`);
      setSelectedCampaign(response.data);
      setCampaignDetailOpen(true);
    } catch (error) {
      toast.error('Failed to load campaign details');
    }
  };

  const StatCard = ({ icon: Icon, title, value, color }) => (
    <Card className="glass-card" data-testid={`email-stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-black font-heading text-foreground mt-1">{value}</p>
          </div>
          <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}20` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="p-6 space-y-6 fade-in" data-testid="email-outreach-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black font-heading tracking-tight text-foreground">Email Outreach</h1>
            <p className="text-muted-foreground mt-2">Send bulk emails to brokers and track campaigns</p>
          </div>
          <Button onClick={() => { fetchBrokers(); fetchStats(); }} variant="outline" data-testid="refresh-outreach">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={Mail} title="Campaigns" value={stats.total_campaigns} color="#3B82F6" />
            <StatCard icon={Send} title="Emails Sent" value={stats.total_emails_sent.toLocaleString()} color="#10B981" />
            <StatCard icon={Users} title="Contacted" value={stats.brokers_contacted.toLocaleString()} color="#F59E0B" />
            <StatCard icon={TrendingUp} title="Response Rate" value="--" color="#8B5CF6" />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Broker Selection */}
          <div className="lg:col-span-2 space-y-4">
            {/* Filters */}
            <Card className="glass-card">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search brokers..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="broker-search"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 rounded-lg bg-background border border-border text-foreground text-sm"
                    data-testid="status-filter"
                  >
                    <option value="">All Status</option>
                    <option value="new">New (Not Contacted)</option>
                    <option value="contacted">Already Contacted</option>
                    <option value="active">Active</option>
                  </select>
                </div>
              </CardContent>
            </Card>

            {/* Broker List */}
            <Card className="glass-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary" />
                    Select Brokers ({selectedBrokers.length} selected)
                  </CardTitle>
                  {brokers.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={selectAllBrokers} data-testid="select-all">
                      {selectedBrokers.length === brokers.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : brokers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No brokers found with current filters</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {brokers.map((broker) => (
                      <div
                        key={broker.id}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedBrokers.includes(broker.id) 
                            ? 'bg-primary/10 ring-1 ring-primary' 
                            : 'bg-background/50 hover:bg-accent/50'
                        }`}
                        onClick={() => toggleSelectBroker(broker.id)}
                        data-testid={`broker-item-${broker.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedBrokers.includes(broker.id)}
                          onChange={() => {}}
                          className="w-4 h-4"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{broker.company_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{broker.email}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          broker.status === 'new' ? 'bg-blue-500/15 text-blue-500' :
                          broker.status === 'contacted' ? 'bg-amber-500/15 text-amber-500' :
                          'bg-emerald-500/15 text-emerald-500'
                        }`}>
                          {broker.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Send Panel */}
          <div className="space-y-4">
            {/* Template Selection */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Email Template
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Select Template</Label>
                  <select
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="w-full mt-2 px-4 py-2 rounded-lg bg-background border border-border text-foreground"
                    data-testid="template-select"
                  >
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                
                {selectedTemplate && (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => loadTemplatePreview(selectedTemplate)}
                    data-testid="preview-template"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview Template
                  </Button>
                )}

                <div className="pt-4 border-t border-border">
                  <Button 
                    className="w-full glow-button"
                    onClick={sendOutreach}
                    disabled={sending || selectedBrokers.length === 0}
                    data-testid="send-outreach"
                  >
                    {sending ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send to {selectedBrokers.length} Brokers
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Recent Campaigns */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  Recent Campaigns
                </CardTitle>
              </CardHeader>
              <CardContent>
                {campaigns.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No campaigns yet</p>
                ) : (
                  <div className="space-y-3">
                    {campaigns.map((campaign) => (
                      <div 
                        key={campaign.id}
                        className="p-3 rounded-lg bg-background/50 hover:bg-accent/50 cursor-pointer transition-colors"
                        onClick={() => viewCampaignDetails(campaign)}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-muted-foreground">
                            {new Date(campaign.created_at).toLocaleDateString()}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            campaign.status === 'completed' ? 'bg-emerald-500/15 text-emerald-500' : 'bg-amber-500/15 text-amber-500'
                          }`}>
                            {campaign.status}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-foreground">
                          {campaign.sent_count} sent / {campaign.failed_count} failed
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Template Preview Dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto glass-card">
            <DialogHeader>
              <DialogTitle>Template Preview: {templatePreview?.name}</DialogTitle>
            </DialogHeader>
            {templatePreview && (
              <div className="space-y-4">
                <div>
                  <Label>Subject</Label>
                  <p className="text-sm text-foreground mt-1 p-2 bg-background rounded">{templatePreview.subject}</p>
                </div>
                <div>
                  <Label>Body</Label>
                  <div 
                    className="mt-1 p-4 bg-white text-black rounded border"
                    dangerouslySetInnerHTML={{ __html: templatePreview.body }}
                  />
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Campaign Detail Dialog */}
        <Dialog open={campaignDetailOpen} onOpenChange={setCampaignDetailOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto glass-card">
            <DialogHeader>
              <DialogTitle>Campaign Details</DialogTitle>
            </DialogHeader>
            {selectedCampaign && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date</Label>
                    <p className="text-sm">{new Date(selectedCampaign.created_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <p className="text-sm">{selectedCampaign.status}</p>
                  </div>
                  <div>
                    <Label>Sent</Label>
                    <p className="text-sm font-bold text-emerald-500">{selectedCampaign.sent_count}</p>
                  </div>
                  <div>
                    <Label>Failed</Label>
                    <p className="text-sm font-bold text-red-500">{selectedCampaign.failed_count}</p>
                  </div>
                </div>
                {selectedCampaign.emails && selectedCampaign.emails.length > 0 && (
                  <div>
                    <Label>Emails Sent</Label>
                    <div className="mt-2 max-h-60 overflow-y-auto space-y-2">
                      {selectedCampaign.emails.slice(0, 50).map((email, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-background/50 rounded text-sm">
                          <span className="truncate">{email.broker_company || email.broker_email}</span>
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default EmailOutreachPage;
